// React & Vendor Libs
const { useState, useMemo, useCallback, useEffect } = wp.element;

// NekoUI
import { NekoButton, NekoBlock, NekoWrapper, NekoColumn, NekoTable, useNekoTasks, NekoPaging,
  NekoCheckbox, NekoTheme, NekoQuickLinks, NekoLink } from '@neko-ui';
import { useRef } from 'react';

import useDatabaseCleanerContext, { postTypesColumns, hideUsedByWordPressLabel, postTypesFilters } from '../context';
import i18n from '../i18n';
import { SectionButtonStyle, limitSelectElement, SectionFooterStyle, FilterSearchField } from './common';
import { useTableData } from './hooks';

const section = 'postTypes';
const SettingsPostTypesTables = () => {

  const { busy, busyAction, listPostTypesTotal: total, filteredListPostTypes, listPostTypesCount,
    postTypesUsedBy, clickedTab, busyCleanBulkTasks, initialized, listPostTypesFilters } = useDatabaseCleanerContext();
  const { mutateListPostTypes, setListPostTypesCount, resetBusyBulkTasks, setBusyAction, setInitialized,
    updateOption, updateListPostTypesFilters } = useDatabaseCleanerContext();

  const enteredCount = clickedTab.PostTypes;
  const bulkTasks = useNekoTasks();

  const [allRefreshedBusy, setAllRefreshedBusy] = useState(false);
  const [refreshBusy, setRefreshBusy] = useState(false);
  const updateBusy = busyAction.some(v => v.item === section);

  useEffect(() => {
    return () => {
      if (busyCleanBulkTasks) return
      bulkTasks.stop();
      resetBusyBulkTasks();
      setBusyAction([]);
    }
  }, [busyCleanBulkTasks]);

  const mutateList = useCallback(async () => {
    setRefreshBusy(true);
    await mutateListPostTypes();
    setRefreshBusy(false);
  }, [mutateListPostTypes]);

  const { data, onAllRefreshClick, onAllTrashClick, disabledAllTrash, updateList, updateCounts, updateUsedBy }
    = useTableData({ bulkTasks, initialList: filteredListPostTypes,
      initialCounts: listPostTypesCount, initialUsedBy: postTypesUsedBy,
      mutateList, onUpdateCounts: setListPostTypesCount, usedbyKey: 'post_type_usedby', section: section });
  useEffect(() => { updateList(filteredListPostTypes) }, [filteredListPostTypes, updateList]);
  useEffect(() => { updateCounts(listPostTypesCount) }, [listPostTypesCount, updateCounts]);
  useEffect(() => { updateUsedBy(postTypesUsedBy) }, [postTypesUsedBy, updateUsedBy]);

  const onAllRefresh = async () => {
    setRefreshBusy(true);
    await mutateListPostTypes();
    await onAllRefreshClick();
    setRefreshBusy(false);
  }

  const allRefresh = useCallback(async({ list = [] }) => {
    setAllRefreshedBusy(true);
    const result = await onAllRefreshClick({exceptManual: true, customList: list});
    if (result) {
      setInitialized("PostTypes", true);
    }
    setAllRefreshedBusy(false);
  }, [onAllRefreshClick]);

  const isRefreshed = useRef(false);

  useEffect(() => {
    if (allRefreshedBusy || initialized.PostTypes) return;
    if (enteredCount === 1 && !isRefreshed.current && filteredListPostTypes.length) {
      isRefreshed.current = true;
      allRefresh({ list: filteredListPostTypes });
    }
  }, [enteredCount, allRefresh, allRefreshedBusy, initialized.PostTypes, filteredListPostTypes]);

  const onUpdateLimit = useCallback(async(value, name) => {
    updateOption({ param: { name, value }});
  }, [updateOption]);

  const limitSelect = useMemo(() => {
    return limitSelectElement("list_post_types_limit", listPostTypesFilters.limit, (value, id) => { updateListPostTypesFilters({ limit: value, page: 1 }); onUpdateLimit(value, id); });
  }, [listPostTypesFilters.limit, updateListPostTypesFilters, onUpdateLimit]);

  const paging = useMemo(() => {
    const { page, limit, filterBy, hideUsedByWordPress } = listPostTypesFilters;
    const pagingTotal = total[filterBy][hideUsedByWordPress];
    return <NekoPaging currentPage={page} limit={limit} total={pagingTotal} onClick={(page) => updateListPostTypesFilters({ page })} />
  }, [listPostTypesFilters.limit, listPostTypesFilters.page, total, onUpdateLimit, updateListPostTypesFilters]);

  const currentSort = useMemo(() => {
    return { by: listPostTypesFilters.sort, accessor: listPostTypesFilters.accessor };
  }, [listPostTypesFilters.accessor, listPostTypesFilters.sort]);

  return (
    <NekoWrapper>

      <NekoColumn minimal>
        <NekoBlock busy={busy || refreshBusy || updateBusy} title="List Post Types" className="primary"
          action={
            <div style={{ display: 'flex' }}>
            <NekoButton icon="trash" title={i18n.INFO_BULK_DELETE} style={SectionButtonStyle}
              onClick={() => onAllTrashClick()} disabled={disabledAllTrash} />
            </div>
          }>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
            <NekoQuickLinks value={listPostTypesFilters.filterBy} 
              onChange={(value) => { updateListPostTypesFilters({ filterBy: value, page: 1 }); }}>
              {postTypesFilters.map(v =>
                <NekoLink title={v.title} value={v.value} count={total[v.value][listPostTypesFilters.hideUsedByWordPress]}
                  variant={v.filterValue === 'warn' && total[v.value][listPostTypesFilters.hideUsedByWordPress] > 0 ? 'danger' : 'primary'} />)
              }
            </NekoQuickLinks>
            <div style={{ display: 'flex' }}>
              <NekoButton icon="replay" title={i18n.INFO_BULK_REFRESH} color={NekoTheme.white}
                style={{...SectionButtonStyle, color: NekoTheme.blue}}
                onClick={onAllRefresh} spinning={refreshBusy} updateBusy={refreshBusy} />
                {paging}
            </div>
          </div>

          <NekoTable variant="raw" alternateRowColor data={data} columns={postTypesColumns}
            selectedItems={[]} sort={currentSort} sortIconColor={NekoTheme.blue}
            onSortChange={(accessor, sort) => {
              updateListPostTypesFilters({ accessor, sort })
            }}
          />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
            {limitSelect}
            {paging}
          </div>

          <div style={SectionFooterStyle}>
            <FilterSearchField id='post_types_search' value={listPostTypesFilters.search}
              onChange={(value) => updateListPostTypesFilters({ search: value, page: 1 })}
              onReset={() => updateListPostTypesFilters({ search: "" })}
            />
            <div style={{ flex: 'auto' }} />
            <NekoCheckbox id="post_types_hide_wordpress" label={hideUsedByWordPressLabel}
              checked={listPostTypesFilters.hideUsedByWordPress}
              onChange={(value) => updateListPostTypesFilters({ hideUsedByWordPress: value, page: 1 })}
            />
          </div>

        </NekoBlock>
      </NekoColumn>

    </NekoWrapper>
  );
};

export default SettingsPostTypesTables;